import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { collectBrowserMetadata } from '../../utils/browserMetadata.js';
import WithdrawalModal from '../modals/WithdrawalModal.js';
import { normalizeQuestionnaireData } from '../../utils/questionnairePages';

// Standalone theme toggle for fragebogen forms (no admin access)
function StandaloneThemeToggle({ theme, setTheme }) {
    const isDark = theme === 'dark';
    return (
        <button
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            onClick={() => setTheme(isDark ? 'light' : 'dark')}
            className="fixed top-4 right-4 z-50 group p-2 rounded-full border border-slate-200 dark:border-slate-600 bg-white/90 dark:bg-slate-700/80 backdrop-blur shadow hover:shadow-md hover:ring-2 ring-slate-200 dark:ring-slate-600 transition"
        >
            <span className="relative block h-6 w-6">
                <svg 
                    className={`${isDark ? 'opacity-0 scale-75 -rotate-90' : 'opacity-100 scale-100 rotate-0'} text-amber-500 drop-shadow-xs transition-all duration-300 ease-out absolute inset-0`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                >
                    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="4" fill="currentColor" />
                        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                    </g>
                </svg>
                <svg 
                    className={`${isDark ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-75 rotate-90'} text-slate-600 dark:text-slate-400 transition-all duration-300 ease-out absolute inset-0`}
                    viewBox="0 0 24 24"
                >
                    <path fill="currentColor" d="M21 12.79A9 9 0 1111.21 3a7 7 0 009.79 9.79z" />
                </svg>
            </span>
        </button>
    );
}

export function FragebogenTakerPage({ fragebogenData, campaignId, onBack, onSubmit }) {
    const normalizedFragebogenData = useMemo(
        () => normalizeQuestionnaireData(fragebogenData),
        [fragebogenData]
    );
    const allQuestions = normalizedFragebogenData.questions || [];

    const [currentStep, setCurrentStep] = useState(1);
    const [contactInfo, setContactInfo] = useState({
        name: '',
        email: '',
        phone: '',
        availability: '',
        consent: false
    });
    const [answers, setAnswers] = useState({});
    const [errors, setErrors] = useState({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [evaluationResult, setEvaluationResult] = useState(null); // { score, explanation, level } from QR/questionnaire scoring
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showErrorScreen, setShowErrorScreen] = useState(false);
    const [hasFailed, setHasFailed] = useState(false);
    const [isEditingContact, setIsEditingContact] = useState(false);
    const [editedContactInfo, setEditedContactInfo] = useState({
        name: '',
        email: '',
        phone: '',
        availability: ''
    });
    const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
    const [isWithdrawn, setIsWithdrawn] = useState(false);
    const [isEditingAvailability, setIsEditingAvailability] = useState(false);
    const [editedAvailability, setEditedAvailability] = useState([]);
    const errorsRef = useRef({});
    
    // Initialize consolidated input log object
    const [inputLog, setInputLog] = useState(() => {
        const uniqueId = campaignId || normalizedFragebogenData?.id || normalizedFragebogenData?.title || 'quiz';
        try {
            const saved = localStorage.getItem(`input_log_${uniqueId}`);
            if (saved) {
                return JSON.parse(saved);
            }
        } catch (error) {
            console.warn('Corrupted localStorage data for input log, resetting:', error);
            try {
                localStorage.removeItem(`input_log_${uniqueId}`);
            } catch (clearError) {
                console.warn('Failed to clear corrupted input log data:', clearError);
            }
        }
        return {};
    });
    
    // Helper function to update consolidated input log
    const updateInputLog = useCallback((questionId, questionText, questionType, value, action = 'UPDATED') => {
        const uniqueId = campaignId || normalizedFragebogenData?.id || normalizedFragebogenData?.title || 'quiz';
        const logEntry = {
            questionText: questionText,
            questionType: questionType,
            currentValue: value,
            action: action,
            timestamp: new Date().toISOString(),
            step: currentStep
        };

        // Update the log object (overwrites old entries for same questionId)
        setInputLog(prevLog => {
            const newInputLog = {
                ...prevLog,
                [questionId]: logEntry
            };
            
            // Save to localStorage
            try {
                localStorage.setItem(`input_log_${uniqueId}`, JSON.stringify(newInputLog));
            } catch (error) {
                console.warn('Could not save input log to localStorage:', error);
            }
            
            return newInputLog;
        });

        return logEntry;
    }, [currentStep, campaignId, normalizedFragebogenData]);
    
    // Theme state for standalone fragebogen forms
    const [theme, setTheme] = useState(() => {
        try {
            const saved = localStorage.getItem('theme');
            if (saved === 'light' || saved === 'dark') return saved;
            if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
        } catch {}
        return 'light';
    });

    // Sync theme with localStorage and apply to document
    useEffect(() => {
        localStorage.setItem('theme', theme);
        // Apply dark mode to html element for global CSS
        if (theme === 'dark') {
            document.documentElement.classList.add('dark');
            document.body.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
            document.body.classList.remove('dark');
        }
    }, [theme]);

    // Set document title based on quiz title
    useEffect(() => {
        // Check if we're on karrieresichern.de domain
        if (window.location.hostname === 'karrieresichern.de') {
            document.title = 'Karrieresichern';
        } else if (normalizedFragebogenData?.title) {
            document.title = normalizedFragebogenData.title;
        } else {
            document.title = 'Quiz';
        }
    }, [normalizedFragebogenData?.title]);

    // Reset answers when quiz data changes
    useEffect(() => {
        setAnswers({});
        setCurrentStep(1);
        setContactInfo({
            name: '',
            email: '',
            phone: '',
            availability: '',
            consent: false
        });
        setIsSubmitted(false);
        setShowErrorScreen(false);
        setHasFailed(false);
    }, [normalizedFragebogenData]);

    // Check if quiz has been submitted (using localStorage)
    useEffect(() => {
        const checkSubmission = () => {
            // Use campaignId for unique localStorage keys, fallback to questionnaire ID or title
            const uniqueId = campaignId || normalizedFragebogenData?.id || normalizedFragebogenData?.title || 'quiz';
            const submissionKey = `quiz_submitted_${uniqueId}`;
            const hasSubmitted = localStorage.getItem(submissionKey) === 'true';
            setIsSubmitted(hasSubmitted);
        };
        
        checkSubmission();
    }, [normalizedFragebogenData, campaignId]);

    // Clear quiz data from localStorage (for testing/development)
    const clearQuizData = useCallback(() => {
        try {
            const uniqueId = campaignId || normalizedFragebogenData?.id || normalizedFragebogenData?.title || 'quiz';
            localStorage.removeItem(`quiz_submitted_${uniqueId}`);
            localStorage.removeItem(`quiz_failed_${uniqueId}`);
            localStorage.removeItem(`input_log_${uniqueId}`);
            // Reset states
            setIsSubmitted(false);
            setHasFailed(false);
            setShowErrorScreen(false);
            console.log('Quiz data cleared for:', uniqueId);
        } catch (error) {
            console.warn('Could not clear quiz data from localStorage:', error);
        }
    }, [campaignId, normalizedFragebogenData]);

    // Expose clear function globally in development mode
    useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            window.clearQuizData = clearQuizData;
            window.clearAllQuizData = () => {
                try {
                    // Clear all quiz-related localStorage entries
                    const keys = Object.keys(localStorage);
                    keys.forEach(key => {
                        if (key.startsWith('quiz_submitted_') || 
                            key.startsWith('quiz_failed_') || 
                            key.startsWith('input_log_')) {
                            localStorage.removeItem(key);
                        }
                    });
                    console.log('All quiz data cleared');
                } catch (error) {
                    console.warn('Could not clear all quiz data:', error);
                }
            };
        }
    }, [clearQuizData]);

    // Check if quiz has already failed (using localStorage)
    useEffect(() => {
        const checkFailure = () => {
            // Use campaignId for unique localStorage keys, fallback to questionnaire ID or title
            const uniqueId = campaignId || normalizedFragebogenData?.id || normalizedFragebogenData?.title || 'quiz';
            const failureKey = `quiz_failed_${uniqueId}`;
            const hasFailedQuiz = localStorage.getItem(failureKey) === 'true';
            setHasFailed(hasFailedQuiz);
            if (hasFailedQuiz) {
                setShowErrorScreen(true);
            }
        };
        
        checkFailure();
    }, [normalizedFragebogenData, campaignId]);

    // Clear errors when contact info changes
    useEffect(() => {
        if (Object.keys(errorsRef.current).length > 0) {
            setErrors({});
            errorsRef.current = {};
        }
    }, [contactInfo.name, contactInfo.email, contactInfo.consent]);

    // Real-time email validation
    const isEmailValid = (email) => {
        if (!email.trim()) return true; // Don't show error for empty field
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const validateContact = (setErrorState = true) => {
        const newErrors = {};
        
        if (!contactInfo.name.trim()) {
            newErrors.name = 'Name ist erforderlich';
        }
        
        if (!contactInfo.email.trim()) {
            newErrors.email = 'E-Mail ist erforderlich';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactInfo.email)) {
            newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
        }
        
        if (!contactInfo.consent) {
            newErrors.consent = 'Sie müssen der Datenschutzerklärung zustimmen';
        }
        
        if (setErrorState) {
            setErrors(newErrors);
            errorsRef.current = newErrors;
        }
        return Object.keys(newErrors).length === 0;
    };

    const questionStepStart = 2;
    const availabilityStep = questionStepStart + allQuestions.length;
    const contactStep = availabilityStep + 1;
    const summaryStep = contactStep + 1;

    const isQuestionStep = currentStep >= questionStepStart && currentStep < availabilityStep;
    const getCurrentQuestion = () => {
        if (!isQuestionStep) return null;
        return allQuestions[currentStep - questionStepStart] || null;
    };

    const handleAnswerSelect = (questionId, value, questionType) => {
        if (questionType === 'multiple-choice') {
            setAnswers(prev => {
                const currentAnswers = prev[questionId] || [];
                const isAdding = !currentAnswers.includes(value);
                
                // Check maxSelections limit if adding a new option
                const question = allQuestions.find(q => String(q.id) === String(questionId));
                if (isAdding && question?.maxSelections && currentAnswers.length >= question.maxSelections) {
                    console.log(`Maximum ${question.maxSelections} selections allowed for this question`);
                    return prev; // Don't update if limit reached
                }
                
                const newAnswers = isAdding 
                    ? [...currentAnswers, value]
                    : currentAnswers.filter(v => v !== value);
                return { ...prev, [questionId]: newAnswers };
            });
        } else {
            setAnswers(prev => ({ ...prev, [questionId]: value }));
        }
        
        
        // Track the answer selection
        const question = allQuestions.find(q => String(q.id) === String(questionId));
        const questionText = question?.text || `Question ${questionId}`;
        const finalValue = questionType === 'multiple-choice' ? 
            (answers[questionId] || []).includes(value) ? 
                (answers[questionId] || []).filter(v => v !== value) : 
                [...(answers[questionId] || []), value] : 
            value;
        
        updateInputLog(questionId, questionText, questionType, finalValue, 'UPDATED');
    };

    // Ranking: select options in order (same as questionnaire); answer = array of option indices in selection order
    const handleRankingChange = (questionId, optionIndex) => {
        const current = answers[questionId] || [];
        const isAdding = !current.includes(optionIndex);
        const newOrder = isAdding
            ? [...current, optionIndex]
            : current.filter(i => i !== optionIndex);
        setAnswers(prev => ({ ...prev, [questionId]: newOrder }));
        const question = allQuestions.find(q => String(q.id) === String(questionId));
        updateInputLog(questionId, question?.text || '', 'ranking', newOrder, isAdding ? 'RANK_ADDED' : 'RANK_REMOVED');
    };

    // Helper function to calculate total steps
    const getTotalSteps = () => {
        return summaryStep; // preview + individual questions + availability + contact + summary
    };

    const isCriticalQuestionAnsweredCorrectly = (question) => {
        if (!question?.critical || !['single-choice', 'multiple-choice'].includes(question.type)) {
            return true;
        }

        const answer = answers[question.id];
        const correctAnswers = question.correctAnswers || [];

        if ((answer === undefined || answer === null || (Array.isArray(answer) && answer.length === 0)) && question.required === false) {
            return true;
        }

        if (question.type === 'single-choice') {
            return correctAnswers.includes(answer);
        }

        if (question.type === 'multiple-choice') {
            if (!Array.isArray(answer) || !Array.isArray(correctAnswers)) return false;
            const sortedAnswer = [...answer].sort();
            const sortedCorrect = [...correctAnswers].sort();
            return sortedAnswer.length === sortedCorrect.length &&
                sortedAnswer.every((value, index) => value === sortedCorrect[index]);
        }

        return true;
    };

    const nextQuestion = () => {
        if (isQuestionStep) {
            const currentQuestion = getCurrentQuestion();
            const isFailedCriticalQuestion = currentQuestion?.critical &&
                ['single-choice', 'multiple-choice'].includes(currentQuestion.type) &&
                !isCriticalQuestionAnsweredCorrectly(currentQuestion);

            if (isFailedCriticalQuestion) {
                const uniqueId = campaignId || normalizedFragebogenData?.id || normalizedFragebogenData?.title || 'quiz';
                const failureKey = `quiz_failed_${uniqueId}`;
                localStorage.setItem(failureKey, 'true');

                setHasFailed(true);
                setShowErrorScreen(true);
                return;
            }
        }
        
        const totalSteps = getTotalSteps();
        if (currentStep < totalSteps) {
            setCurrentStep(prev => prev + 1);
        }
    };

    const isQuestionAnswered = (question) => {
        const answer = answers[question.id];
        
        if (question.type === 'free-text' || question.type === 'text') {
            return answer && String(answer).trim().length > 0;
        } else if (question.type === 'multiple-choice') {
            return Array.isArray(answer) && answer.length > 0;
        } else if (question.type === 'ranking') {
            return Array.isArray(answer) && answer.length === (question.options || []).length;
        } else if (question.type === 'image') {
            const at = question.answerType || 'text';
            if (at === 'text') return answer && String(answer).trim().length > 0;
            if (at === 'single-choice') return answer !== undefined && answer !== null;
            if (at === 'multiple-choice') return Array.isArray(answer) && answer.length > 0;
            return false;
        } else {
            return answer !== undefined && answer !== null;
        }
    };

    const canProceed = () => {
        if (currentStep === 1) {
            return true; // Preview step
        } else if (isQuestionStep) {
            const currentQuestion = getCurrentQuestion();
            if (!currentQuestion || currentQuestion.required === false) return true;
            return isQuestionAnswered(currentQuestion);
        } else if (currentStep === availabilityStep) {
            return true; // Availability step
        } else if (currentStep === contactStep) {
            return validateContact(false); // Contact form step
        }
        return true; // Summary step
    };

    const handleSubmit = async () => {
        const allAnswers = {
            ...answers,
            availability: answers.availability || []
        };
        const browserMetadata = collectBrowserMetadata();
        const submission = {
            contactInfo,
            answers: allAnswers,
            fragebogenId: normalizedFragebogenData.id || 'quiz',
            submittedAt: new Date().toISOString(),
            answerLog: {
                applicantName: contactInfo.name || 'Not provided',
                questionnaireId: campaignId || normalizedFragebogenData?.id || 'quiz',
                interactions: inputLog
            },
            meta_data: browserMetadata
        };

        const uniqueId = campaignId || normalizedFragebogenData?.id || normalizedFragebogenData?.title || 'quiz';
        const submissionKey = `quiz_submitted_${uniqueId}`;
        localStorage.setItem(submissionKey, 'true');

        setIsSubmitting(true);
        try {
            const result = await Promise.resolve(onSubmit(submission));
            if (result && typeof result === 'object' && (result.score !== undefined || result.level)) {
                setEvaluationResult(result);
            }
        } catch (err) {
            console.warn('Submit callback error:', err);
        } finally {
            setIsSubmitting(false);
            setIsSubmitted(true);
        }
    };

    const handleWithdrawalRequest = () => {
        setShowWithdrawalModal(true);
    };

    const handleConfirmWithdrawal = () => {
        setIsWithdrawn(true);
        setShowWithdrawalModal(false);
        // Mark quiz as withdrawn in localStorage
        const uniqueId = campaignId || normalizedFragebogenData?.id || normalizedFragebogenData?.title || 'quiz';
        const withdrawalKey = `quiz_withdrawn_${uniqueId}`;
        localStorage.setItem(withdrawalKey, 'true');
    };

    const handleStayInPool = () => {
        setShowWithdrawalModal(false);
        // Just close the modal, user stays in the questionnaire
    };

    const handleEditContact = () => {
        setEditedContactInfo({
            name: contactInfo.name,
            email: contactInfo.email,
            phone: contactInfo.phone,
            availability: contactInfo.availability
        });
        setIsEditingContact(true);
    };

    const handleSaveContact = () => {
        setContactInfo(prev => ({
            ...prev,
            ...editedContactInfo
        }));
        setIsEditingContact(false);
    };

    const handleCancelEdit = () => {
        setIsEditingContact(false);
        setEditedContactInfo({
            name: '',
            email: '',
            phone: '',
            availability: ''
        });
    };

    const renderContactForm = () => (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4 relative z-10">
            <StandaloneThemeToggle theme={theme} setTheme={setTheme} />
            <div className="w-full max-w-2xl">
                <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-xs rounded-xl p-8 shadow-xl min-h-[600px] flex flex-col justify-center border border-slate-100 dark:border-slate-700">
                    {/* Progress Bar and Step Counter */}
                    <div className="mb-8">
                        <div className="flex justify-between items-center">
                            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3 mr-2.5 mx-2">
                                <div 
                                    className="h-3 rounded-full transition-all duration-300"
                                    style={{ 
                                        width: `${(contactStep / getTotalSteps()) * 100}%`,
                                        background: 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)',
                                        boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
                                    }}
                                ></div>
                            </div>
                            <div className="bg-slate-100 dark:bg-slate-600 px-3 py-1 rounded-full shadow-xs whitespace-nowrap">
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-300 dark:text-slate-300">
                                    {contactStep} / {getTotalSteps()}
                                </span>
                            </div>
                        </div>
                    </div>

                        <div className="text-center mb-8">
                            <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100 dark:text-slate-100 mb-4">
                                Kontaktdaten
                            </h1>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 dark:text-slate-300 mb-2">
                                    Vollständiger Name *
                                </label>
                                <input
                                    type="text"
                                    value={contactInfo.name}
                                    onChange={(e) => {
                                        const newName = e.target.value;
                                        setContactInfo(prev => ({ ...prev, name: newName }));
                                        updateInputLog('applicant_name', 'Ihr Name', 'text', newName, 'NAME_UPDATED');
                                    }}
                                    className={`w-full px-4 py-3 border rounded-lg text-slate-800 dark:text-slate-100 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 bg-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                                        errors.name ? 'border-red-400' : 'border-slate-300 dark:border-slate-600 dark:border-slate-600'
                                    }`}
                                    placeholder="Ihr vollständiger Name"
                                />
                                {errors.name && (
                                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 dark:text-slate-300 mb-2">
                                    E-Mail-Adresse *
                                </label>
                                <input
                                    type="email"
                                    value={contactInfo.email}
                                    onChange={(e) => {
                                        const newEmail = e.target.value;
                                        setContactInfo(prev => ({ ...prev, email: newEmail }));
                                        updateInputLog('applicant_email', 'E-Mail-Adresse', 'email', newEmail, 'UPDATED');
                                    }}
                                    className={`w-full px-4 py-3 border rounded-lg text-slate-800 dark:text-slate-100 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 bg-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                                        errors.email || (!isEmailValid(contactInfo.email) && contactInfo.email.trim()) ? 'border-red-400' : 'border-slate-300 dark:border-slate-600 dark:border-slate-600'
                                    }`}
                                    placeholder="ihre.email@beispiel.de"
                                />
                                {errors.email && (
                                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                                )}
                                {!isEmailValid(contactInfo.email) && contactInfo.email.trim() && !errors.email && (
                                    <p className="mt-1 text-sm text-amber-600 dark:text-amber-400">
                                        ⚠️ Bitte geben Sie eine gültige E-Mail-Adresse ein
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 dark:text-slate-300 mb-2">
                                    Telefonnummer (optional)
                                </label>
                                <input
                                    type="tel"
                                    value={contactInfo.phone}
                                    onChange={(e) => {
                                        const newPhone = e.target.value;
                                        setContactInfo(prev => ({ ...prev, phone: newPhone }));
                                        updateInputLog('applicant_phone', 'Telefonnummer', 'tel', newPhone, 'UPDATED');
                                    }}
                                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 dark:border-slate-600 rounded-lg text-slate-800 dark:text-slate-100 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 bg-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                    placeholder="+49 123 456 7890"
                                />
                            </div>


                            <div>
                                <label className="flex items-start space-x-3">
                                    <input
                                        type="checkbox"
                                        checked={contactInfo.consent}
                                        onChange={(e) => {
                                            const newConsent = e.target.checked;
                                            setContactInfo(prev => ({ ...prev, consent: newConsent }));
                                            updateInputLog('applicant_consent', 'Datenschutzerklärung', 'checkbox', newConsent, 'UPDATED');
                                        }}
                                        className={`mt-1 w-4 h-4 text-blue-600 bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 dark:border-slate-600 rounded focus:ring-blue-500 ${
                                            errors.consent ? 'border-red-500' : ''
                                        }`}
                                    />
                                    <span className="text-sm text-slate-700 dark:text-slate-300 dark:text-slate-300">
                                        Ich stimme der Verarbeitung meiner Daten gemäß der{' '}
                                        <a 
                                            href="https://ei4heidelberg.de/datenschutz"
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline"
                                        >
                                            Datenschutzerklärung
                                        </a>{' '}
                                        zu. *
                                    </span>
                                </label>
                                {errors.consent && (
                                    <p className="mt-1 text-sm text-red-600">{errors.consent}</p>
                                )}
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                                    <span className="text-red-500 font-bold">*</span>
                                    <span>Erforderlich</span>
                                </div>
                                <button
                                    onClick={nextQuestion}
                                    className="px-8 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none quiz-navigation-button"
                                    disabled={!canProceed()}
                                >
                                    Weiter
                                </button>
                            </div>
                        </div>
                </div>
            </div>
        </div>
    );

    const renderAvailability = () => (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4 relative z-10">
            <StandaloneThemeToggle theme={theme} setTheme={setTheme} />
            <div className="w-full max-w-2xl">
                <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-xs rounded-xl p-8 shadow-xl min-h-[600px] flex flex-col justify-center border border-slate-100 dark:border-slate-700">
                    {/* Progress Bar and Step Counter */}
                    <div className="mb-8">
                        <div className="flex justify-between items-center">
                            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3 mr-2.5 mx-2">
                                <div 
                                    className="h-3 rounded-full transition-all duration-300"
                                    style={{ 
                                        width: `${(availabilityStep / getTotalSteps()) * 100}%`,
                                        background: 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)',
                                        boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
                                    }}
                                ></div>
                            </div>
                            <div className="bg-slate-100 dark:bg-slate-600 px-3 py-1 rounded-full shadow-xs whitespace-nowrap">
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                    {availabilityStep} / {getTotalSteps()}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                            Deine Erreichbarkeit
                        </h1>
                        <p className="text-slate-600 dark:text-slate-400 text-lg">
                            Wann können wir dich am besten erreichen?
                        </p>
                    </div>

                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                Verfügbare Zeiten
                            </label>
                            <div className="space-y-3">
                                {['morgens', 'vormittags', 'mittags', 'nachmittags', 'abends'].map((time) => (
                                    <label key={time} className="flex items-center space-x-3 p-3 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer transition-colors">
                                        <input
                                            type="checkbox"
                                            checked={answers.availability?.includes(time) || false}
                                            onChange={(e) => {
                                                const currentAvailability = answers.availability || [];
                                                let newAvailability;
                                                if (e.target.checked) {
                                                    newAvailability = [...currentAvailability, time];
                                                } else {
                                                    newAvailability = currentAvailability.filter(t => t !== time);
                                                }
                                                setAnswers(prev => ({ ...prev, availability: newAvailability }));
                                                updateInputLog('availability', 'Verfügbarkeit', 'checkbox', newAvailability.join(', '), 'UPDATED');
                                            }}
                                            className="w-4 h-4 text-blue-600 bg-slate-100 border-slate-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-slate-800 focus:ring-2 dark:bg-slate-700 dark:border-slate-600"
                                        />
                                        <span className="text-slate-800 dark:text-slate-200 capitalize">
                                            {time === 'morgens' && 'Morgens (6-9 Uhr)'}
                                            {time === 'vormittags' && 'Vormittags (9-12 Uhr)'}
                                            {time === 'mittags' && 'Mittags (12-14 Uhr)'}
                                            {time === 'nachmittags' && 'Nachmittags (14-18 Uhr)'}
                                            {time === 'abends' && 'Abends (18-22 Uhr)'}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 flex justify-end">
                        <button
                            onClick={nextQuestion}
                            className="px-8 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none disabled:hover:translate-y-0"
                        >
                            Weiter
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderPreview = () => (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4 relative z-10">
            <StandaloneThemeToggle theme={theme} setTheme={setTheme} />
            <div className="w-full max-w-2xl">
                <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-xs rounded-xl p-8 shadow-xl min-h-[600px] flex flex-col justify-center border border-slate-100 dark:border-slate-700">
                    {/* Progress Bar and Step Counter */}
                    <div className="mb-8">
                        <div className="flex justify-between items-center">
                            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3 mr-2.5 mx-2">
                                <div 
                                    className="h-3 rounded-full transition-all duration-300"
                                    style={{ 
                                        width: `${(1 / getTotalSteps()) * 100}%`,
                                        background: 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)',
                                        boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
                                    }}
                                ></div>
                            </div>
                            <div className="bg-slate-100 dark:bg-slate-600 px-3 py-1 rounded-full shadow-xs whitespace-nowrap">
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                    1 / {getTotalSteps()}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Quiz Title */}
                    {normalizedFragebogenData.title && (
                        <div className="text-center mb-6">
                            <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                                {normalizedFragebogenData.title}
                            </h1>
                        </div>
                    )}

                    {/* Introduction Text */}
                    {normalizedFragebogenData.description && (
                        <div className="mb-8">
                            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed text-center">
                                {normalizedFragebogenData.description}
                            </p>
                        </div>
                    )}

                    <h2 className="text-2xl font-bold text-blue-800 mb-6">
                        {normalizedFragebogenData.previewTitle || "Was Sie erwartet"}
                    </h2>
                        
                        <div className="mb-8">
                            <ul className="space-y-3 text-slate-700 dark:text-slate-300">
                                <li className="flex items-start">
                                    <span className="w-2 h-2 bg-slate-400 rounded-full mt-2 mr-3 shrink-0"></span>
                                    {normalizedFragebogenData.previewDescription || "Beantworten Sie verschiedene Fragetypen."}
                                </li>
                            </ul>
                        </div>

                        <div className="flex justify-center">
                            <button
                                onClick={nextQuestion}
                                className="px-8 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 hover:shadow-lg transition-all duration-200"
                            >
                                Beginnen
                            </button>
                        </div>
                </div>
            </div>
        </div>
    );

    // Helper function to render answer summary
    const renderAnswerSummary = (question, answer) => {
        // Debug logging
        console.log('Rendering answer summary:', { question: question.text, answer, type: question.type, options: question.options });
        
        // Check if answer is undefined, null, or empty array
        // For single-choice, 0 is a valid answer (first option)
        if (answer === undefined || answer === null || (Array.isArray(answer) && answer.length === 0)) {
            return <span className="text-slate-400 italic">Nicht beantwortet</span>;
        }

        switch (question.type) {
            case 'single-choice':
                // Handle both number and string indices
                const answerIndex = typeof answer === 'string' ? parseInt(answer, 10) : answer;
                if (typeof answerIndex === 'number' && !isNaN(answerIndex) && question.options && question.options[answerIndex] !== undefined) {
                    return <span>{question.options[answerIndex]}</span>;
                }
                // Fallback to showing the raw answer if options don't exist
                return <span>{String(answer)}</span>;
                
            case 'multiple-choice':
                if (Array.isArray(answer)) {
                    // Special handling for availability question (stores actual values, not indices)
                    if (question.id === 'availability') {
                        const selectedOptions = answer.map(value => {
                            // Map the stored values to display names
                            const displayNames = {
                                'morgens': 'Morgens (6-9 Uhr)',
                                'vormittags': 'Vormittags (9-12 Uhr)',
                                'mittags': 'Mittags (12-14 Uhr)',
                                'nachmittags': 'Nachmittags (14-18 Uhr)',
                                'abends': 'Abends (18-22 Uhr)'
                            };
                            return displayNames[value] || value;
                        });
                        return (
                            <div className="space-y-1">
                                {selectedOptions.map((option, i) => (
                                    <div key={i} className="flex items-center">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                                        <span>{option}</span>
                                    </div>
                                ))}
                            </div>
                        );
                    } else {
                        // Regular multiple-choice questions (store indices)
                        const selectedOptions = answer.map(index => 
                            question.options && question.options[index] ? question.options[index] : `Option ${index + 1}`
                        );
                        return (
                            <div className="space-y-1">
                                {selectedOptions.map((option, i) => (
                                    <div key={i} className="flex items-center">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                                        <span>{option}</span>
                                    </div>
                                ))}
                            </div>
                        );
                    }
                }
                return <span>{answer}</span>;
                
            case 'free-text':
            case 'text':
                return (
                    <div className="bg-slate-50 dark:bg-slate-700 p-3 rounded border border-slate-200 dark:border-slate-600 text-sm text-slate-800 dark:text-slate-200">
                        {answer}
                    </div>
                );
                
            case 'ranking':
                if (Array.isArray(answer) && question.options) {
                    const ordered = answer.map(idx => question.options[idx]).filter(Boolean);
                    return (
                        <div className="space-y-1">
                            {ordered.map((opt, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <span className="text-slate-500 dark:text-slate-400 font-medium">{i + 1}.</span>
                                    <span>{opt}</span>
                                </div>
                            ))}
                        </div>
                    );
                }
                return <span>{String(answer)}</span>;

            case 'image':
                if (question.answerType === 'single-choice' && question.options && (answer === 0 || answer)) {
                    const idx = typeof answer === 'string' ? parseInt(answer, 10) : answer;
                    return <span>{question.options[idx]}</span>;
                }
                if (question.answerType === 'multiple-choice' && Array.isArray(answer) && question.options) {
                    const selected = answer.map(i => question.options[i]).filter(Boolean);
                    return <span>{selected.join(', ')}</span>;
                }
                return (
                    <div className="bg-slate-50 dark:bg-slate-700 p-3 rounded border border-slate-200 dark:border-slate-600 text-sm text-slate-800 dark:text-slate-200">
                        {answer}
                    </div>
                );
                
            default:
                return <span>{String(answer)}</span>;
        }
    };


    const renderQuestionInput = (question) => {
        if (question.type === 'image') {
            return (
                <div className="space-y-6">
                    {(question.imageDataUrl || question.imageUrl) && (
                        <div className="flex justify-center">
                            <img src={question.imageDataUrl || question.imageUrl} alt="" className="max-h-64 rounded-lg border border-slate-200 dark:border-slate-600" />
                        </div>
                    )}
                    {question.answerType === 'single-choice' && (
                        <div className="space-y-2">
                            {(question.options || []).map((option, index) => (
                                <label key={index} className="flex items-center p-3 border border-slate-300 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer gap-3">
                                    <input
                                        type="radio"
                                        name={`single-choice-${question.id}`}
                                        value={index}
                                        checked={answers[question.id] === index}
                                        onChange={() => handleAnswerSelect(question.id, index, 'single-choice')}
                                        className="h-5 w-5 text-green-600 rounded focus:ring-green-500 shrink-0 shrink-0"
                                    />
                                    <span className="text-slate-700 dark:text-slate-200 text-base">{option}</span>
                                </label>
                            ))}
                        </div>
                    )}
                    {question.answerType === 'multiple-choice' && (
                        <div className="space-y-2">
                            {(question.options || []).map((option, index) => {
                                const isSelected = (answers[question.id] || []).includes(index);
                                const isLimitReached = question.maxSelections && !isSelected && (answers[question.id] || []).length >= question.maxSelections;
                                return (
                                    <label
                                        key={index}
                                        className={`flex items-center p-3 border rounded-lg gap-3 transition-all duration-200 ${
                                            isLimitReached
                                                ? 'border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 cursor-not-allowed opacity-60'
                                                : 'border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            value={index}
                                            checked={isSelected}
                                            onChange={() => handleAnswerSelect(question.id, index, 'multiple-choice')}
                                            disabled={isLimitReached}
                                            className="h-5 w-5 text-green-600 rounded focus:ring-green-500 shrink-0 shrink-0 disabled:opacity-50"
                                        />
                                        <span className={`text-base ${isLimitReached ? 'text-slate-400 dark:text-slate-500' : 'text-slate-700 dark:text-slate-200'}`}>{option}</span>
                                    </label>
                                );
                            })}
                        </div>
                    )}
                    {(question.answerType || 'text') === 'text' && (
                        <textarea
                            value={answers[question.id] || ''}
                            onChange={(e) => {
                                const newValue = e.target.value;
                                setAnswers(prev => ({ ...prev, [question.id]: newValue }));
                                updateInputLog(question.id, question.text, 'image', newValue, 'UPDATED');
                            }}
                            className="w-full p-3 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                            rows={4}
                            placeholder="Ihre Antwort hier..."
                        />
                    )}
                </div>
            );
        }

        if (question.type === 'single-choice') {
            return (
                <div className="space-y-2">
                    {(question.options || []).map((option, index) => (
                        <label key={index} className="flex items-center p-3 border border-slate-300 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer gap-3">
                            <input
                                type="radio"
                                name={`single-choice-${question.id}`}
                                value={index}
                                checked={answers[question.id] === index}
                                onChange={() => handleAnswerSelect(question.id, index, 'single-choice')}
                                className="h-5 w-5 text-green-600 rounded focus:ring-green-500 shrink-0 shrink-0"
                            />
                            <span className="text-slate-700 dark:text-slate-200 text-base">{option}</span>
                        </label>
                    ))}
                </div>
            );
        }

        if (question.type === 'multiple-choice') {
            return (
                <div className="space-y-2">
                    {(question.options || []).map((option, index) => {
                        const isSelected = (answers[question.id] || []).includes(index);
                        const isLimitReached = question.maxSelections && !isSelected && (answers[question.id] || []).length >= question.maxSelections;
                        return (
                            <label
                                key={index}
                                className={`flex items-center p-3 border rounded-lg gap-3 transition-all duration-200 ${
                                    isLimitReached
                                        ? 'border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 cursor-not-allowed opacity-60'
                                        : 'border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer'
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    value={index}
                                    checked={isSelected}
                                    onChange={() => handleAnswerSelect(question.id, index, 'multiple-choice')}
                                    disabled={isLimitReached}
                                    className="h-5 w-5 text-green-600 rounded focus:ring-green-500 shrink-0 shrink-0 disabled:opacity-50"
                                />
                                <span className={`text-base ${isLimitReached ? 'text-slate-400 dark:text-slate-500' : 'text-slate-700 dark:text-slate-200'}`}>{option}</span>
                            </label>
                        );
                    })}
                </div>
            );
        }

        if (question.type === 'ranking' && (question.options || []).length > 0) {
            return (
                <div className="space-y-2">
                    {(question.options || []).map((opt, optIndex) => {
                        const selectedOrder = answers[question.id] || [];
                        const position = selectedOrder.indexOf(optIndex) + 1;
                        const isSelected = position > 0;
                        return (
                            <button
                                key={optIndex}
                                type="button"
                                onClick={() => handleRankingChange(question.id, optIndex)}
                                className="flex items-center p-3 border border-slate-300 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 gap-3 w-full text-left transition-colors"
                            >
                                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 font-bold text-sm shrink-0 transition-colors ${
                                    isSelected
                                        ? 'bg-blue-600 border-blue-600 text-white'
                                        : 'border-slate-300 dark:border-slate-600 text-slate-400 dark:text-slate-500'
                                }`}>
                                    {isSelected ? position : ''}
                                </div>
                                <span className="text-slate-700 dark:text-slate-200 text-base flex-1">{opt}</span>
                            </button>
                        );
                    })}
                </div>
            );
        }

        return (
            <textarea
                value={answers[question.id] || ''}
                onChange={(e) => {
                    const newValue = e.target.value;
                    setAnswers(prev => ({ ...prev, [question.id]: newValue }));
                    updateInputLog(question.id, question.text, question.type, newValue, 'UPDATED');
                }}
                className="w-full p-3 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                rows={5}
                placeholder="Ihre Antwort hier..."
            />
        );
    };

    const renderQuestion = () => {
        const currentQuestion = getCurrentQuestion();
        if (!currentQuestion) return null;
        const currentQuestionNumber = currentStep - questionStepStart + 1;

        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4 relative z-10">
                <StandaloneThemeToggle theme={theme} setTheme={setTheme} />
                <div className="w-full max-w-2xl">
                    <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-xs rounded-xl p-8 shadow-xl min-h-[600px] flex flex-col border border-slate-100 dark:border-slate-700">
                        <div className="mb-8">
                            <div className="flex justify-between items-center">
                                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3 mr-2.5 mx-2">
                                    <div
                                        className="h-3 rounded-full transition-all duration-300"
                                        style={{
                                            width: `${(currentStep / getTotalSteps()) * 100}%`,
                                            background: 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)',
                                            boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
                                        }}
                                    ></div>
                                </div>
                                <div className="bg-slate-100 dark:bg-slate-600 px-3 py-1 rounded-full shadow-xs whitespace-nowrap">
                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                        {currentStep} / {getTotalSteps()}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="mb-6">
                            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                                {`Frage ${currentQuestionNumber}`}
                            </h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                {`${currentQuestionNumber} von ${allQuestions.length}`}
                            </p>
                        </div>

                        <div className="space-y-6 flex-1 overflow-y-auto pr-1">
                            <div key={currentQuestion.id} className="border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">
                                    {currentQuestion.text} {currentQuestion.required !== false ? <span className="text-red-500">*</span> : null}
                                </h3>
                                {renderQuestionInput(currentQuestion)}
                            </div>
                        </div>

                        <div className="flex justify-end pt-6">
                            <button
                                onClick={nextQuestion}
                                className="px-8 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={currentStep >= getTotalSteps() || !canProceed()}
                            >
                                Weiter
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderConclusion = () => {
        const levelLabels = {
            'Not suitable': 'Nicht geeignet',
            'Marginal': 'Eingeschränkt geeignet',
            'Partially suitable': 'Teilweise geeignet',
            'Suitable': 'Geeignet',
            'Highly suitable': 'Sehr gut geeignet'
        };
        const levelLabel = evaluationResult?.level ? (levelLabels[evaluationResult.level] || evaluationResult.level) : null;
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4 relative z-10">
                <StandaloneThemeToggle theme={theme} setTheme={setTheme} />
                <div className="w-full max-w-2xl">
                    <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-xs rounded-xl p-8 shadow-xl min-h-[600px] flex flex-col justify-center border border-slate-100 dark:border-slate-700">
                        <div className="text-center">
                            <div className="mb-8">
                                <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                                    Vielen Dank!
                                </h1>
                            </div>
                            
                            {evaluationResult && (evaluationResult.score !== undefined || evaluationResult.level) && (
                                <div className="mb-6 p-4 rounded-xl bg-base-200 border border-base-300 text-left">
                                    <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">Ihre Eignungsbewertung</h2>
                                    <div className="flex flex-wrap items-center gap-3 mb-2">
                                        {evaluationResult.score !== undefined && (
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-lg font-medium bg-primary/20 text-primary">
                                                {Math.round(evaluationResult.score)}/10
                                            </span>
                                        )}
                                        {levelLabel && (
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-base-300 text-base-content">
                                                {levelLabel}
                                            </span>
                                        )}
                                    </div>
                                    {evaluationResult.explanation && (
                                        <p className="text-sm text-slate-600 dark:text-slate-400">{evaluationResult.explanation}</p>
                                    )}
                                </div>
                            )}
                            
                            <div className="text-lg text-slate-600 dark:text-slate-400 mb-8 leading-relaxed">
                                {normalizedFragebogenData.conclusionText || "Vielen Dank für Ihre Zeit und die sorgfältigen Antworten. Wir haben Ihre Unterlagen erhalten und werden sie nun intern prüfen."}
                            </div>
                            
                            <div className="text-sm text-slate-500">
                                Sie können diese Seite jetzt schließen.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderSummary = () => {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4 relative z-10">
                <StandaloneThemeToggle theme={theme} setTheme={setTheme} />
                <div className="w-full max-w-2xl">
                    <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-xs rounded-xl p-8 shadow-xl min-h-[600px] flex flex-col justify-center border border-slate-100 dark:border-slate-700">
                        {/* Progress Bar and Step Counter */}
                        <div className="mb-8">
                            <div className="flex justify-between items-center">
                                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3 mr-2.5 mx-2">
                                    <div 
                                        className="h-3 rounded-full transition-all duration-300"
                                        style={{ 
                                            width: '100%',
                                            background: 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)',
                                            boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
                                        }}
                                    ></div>
                                </div>
                                <div className="bg-slate-100 dark:bg-slate-600 px-3 py-1 rounded-full shadow-xs whitespace-nowrap">
                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                        {getTotalSteps()} / {getTotalSteps()}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="text-center mb-8">
                            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                                Vielen Dank, {contactInfo.name}!
                            </h2>
                            <p className="text-slate-600 dark:text-slate-400">
                                Zusammenfassung Ihrer Antworten:
                            </p>
                        </div>

                        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg p-8 shadow-xs mb-8">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                                    Ihre Kontaktdaten
                                </h3>
                                {!isEditingContact && (
                                    <button
                                        onClick={handleEditContact}
                                        className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                                    >
                                        Bearbeiten
                                    </button>
                                )}
                            </div>
                            
                            {isEditingContact ? (
                                <div className="space-y-4 mb-6">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                            Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={editedContactInfo.name}
                                            onChange={(e) => setEditedContactInfo(prev => ({ ...prev, name: e.target.value }))}
                                            className="w-full p-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                            E-Mail *
                                        </label>
                                        <input
                                            type="email"
                                            value={editedContactInfo.email}
                                            onChange={(e) => setEditedContactInfo(prev => ({ ...prev, email: e.target.value }))}
                                            className="w-full p-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                            Telefon
                                        </label>
                                        <input
                                            type="tel"
                                            value={editedContactInfo.phone}
                                            onChange={(e) => setEditedContactInfo(prev => ({ ...prev, phone: e.target.value }))}
                                            className="w-full p-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                            Erreichbarkeit
                                        </label>
                                        <select
                                            value={editedContactInfo.availability}
                                            onChange={(e) => setEditedContactInfo(prev => ({ ...prev, availability: e.target.value }))}
                                            className="w-full p-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="">Bitte wählen...</option>
                                            <option value="morgens">Morgens</option>
                                            <option value="vormittags">Vormittags</option>
                                            <option value="mittags">Mittags</option>
                                            <option value="nachmittags">Nachmittags</option>
                                            <option value="abends">Abends</option>
                                        </select>
                                    </div>
                                    <div className="flex gap-2 pt-2">
                                        <button
                                            onClick={handleSaveContact}
                                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                        >
                                            Speichern
                                        </button>
                                        <button
                                            onClick={handleCancelEdit}
                                            className="px-4 py-2 bg-slate-500 text-white rounded-lg hover:bg-slate-600 transition-colors"
                                        >
                                            Abbrechen
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-2 text-slate-600 dark:text-slate-400 mb-6">
                                    <p><strong>Name:</strong> {contactInfo.name}</p>
                                    <p><strong>E-Mail:</strong> {contactInfo.email}</p>
                                    {contactInfo.phone && <p><strong>Telefon:</strong> {contactInfo.phone}</p>}
                                    {/* Erreichbarkeit is part of quiz; omit here */}
                                </div>
                            )}

                            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
                                Ihre Antworten
                            </h3>
                            <div className="space-y-4">
                                {/* Availability question */}
                                <div className="border-b border-slate-100 dark:border-slate-600 pb-4">
                                    <div className="flex items-start justify-between mb-2">
                                        <p className="font-medium text-slate-800 dark:text-slate-100">
                                            1. Deine Erreichbarkeit
                                        </p>
                                        {!isEditingAvailability && (
                                            <button
                                                onClick={() => {
                                                    setEditedAvailability(Array.isArray(answers.availability) ? answers.availability : []);
                                                    setIsEditingAvailability(true);
                                                }}
                                                className="text-sm px-2 py-1 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50"
                                            >
                                                Bearbeiten
                                            </button>
                                        )}
                                    </div>
                                    <div className="text-slate-600 dark:text-slate-400">
                                        {isEditingAvailability ? (
                                            <div className="space-y-2">
                                                {['morgens','vormittags','mittags','nachmittags','abends'].map(opt => (
                                                    <label key={opt} className="flex items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={editedAvailability.includes(opt)}
                                                            onChange={(e) => {
                                                                const checked = e.target.checked;
                                                                setEditedAvailability(prev => checked
                                                                    ? [...prev, opt]
                                                                    : prev.filter(v => v !== opt)
                                                                );
                                                            }}
                                                        />
                                                        <span className="capitalize">{opt}</span>
                                                    </label>
                                                ))}
                                                <div className="flex gap-2 pt-2">
                                                    <button
                                                        onClick={() => {
                                                            setAnswers(prev => ({ ...prev, availability: editedAvailability }));
                                                            setIsEditingAvailability(false);
                                                        }}
                                                        className="px-3 py-1 rounded-lg bg-green-600 text-white hover:bg-green-700"
                                                    >
                                                        Speichern
                                                    </button>
                                                    <button
                                                        onClick={() => setIsEditingAvailability(false)}
                                                        className="px-3 py-1 rounded-lg bg-slate-500 text-white hover:bg-slate-600"
                                                    >
                                                        Abbrechen
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            renderAnswerSummary({
                                                id: 'availability',
                                                type: 'multiple-choice',
                                                options: ['morgens', 'vormittags', 'mittags', 'nachmittags', 'abends']
                                            }, answers.availability)
                                        )}
                                    </div>
                                </div>
                                
                                {/* Questionnaire questions */}
                                {normalizedFragebogenData.questions.map((question, index) => {
                                    const answer = answers[question.id];
                                    return (
                                        <div key={question.id} className="border-b border-slate-100 dark:border-slate-600 pb-4 last:border-b-0">
                                            <p className="font-medium text-slate-800 dark:text-slate-100 mb-2">
                                                {index + 2}. {question.text}
                                            </p>
                                            <div className="text-slate-600 dark:text-slate-400">
                                                {renderAnswerSummary(question, answer)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="flex justify-between gap-4">
                            <button
                                onClick={handleWithdrawalRequest}
                                className="px-6 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 hover:shadow-lg transition-all duration-200"
                            >
                                Bewerbung zurückziehen
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className="px-8 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 hover:shadow-lg transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? 'Wird eingereicht...' : 'Bewerbung einreichen'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Small footer component
    const renderFooter = () => (
        <footer className="mt-8 pt-4 border-t border-slate-200 dark:border-slate-700">
            <div className="flex justify-center items-center text-xs text-slate-500 dark:text-slate-400">
                <div className="flex space-x-4">
                    <a
                        href="https://ei4heidelberg.de/impressum"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                    >
                        Impressum
                    </a>
                    <a
                        href="https://ei4heidelberg.de/datenschutz"
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                    >
                        Datenschutz
                    </a>
                </div>
            </div>
        </footer>
    );

    // If quiz has been submitted, show conclusion
    if (isSubmitted) {
        return (
            <>
                {renderConclusion()}
                {renderFooter()}
            </>
        );
    }

    // If quiz has been withdrawn, show withdrawal confirmation
    if (isWithdrawn) {
        return (
            <>
                {renderWithdrawalConfirmation()}
                {renderFooter()}
            </>
        );
    }

    function renderWithdrawalConfirmation() {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4 relative z-10">
                <StandaloneThemeToggle theme={theme} setTheme={setTheme} />
                <div className="w-full max-w-2xl">
                    <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-xs rounded-xl p-8 shadow-xl min-h-[600px] flex flex-col justify-center border border-slate-100 dark:border-slate-700">
                        {/* Progress Bar and Step Counter */}
                        <div className="mb-8">
                            <div className="flex justify-between items-center">
                                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3 mr-2.5 mx-2">
                                    <div 
                                        className="h-3 rounded-full transition-all duration-300"
                                        style={{ 
                                            width: '100%',
                                            background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)',
                                            boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)'
                                        }}
                                    ></div>
                                </div>
                                <div className="bg-orange-100 dark:bg-orange-900/30 px-3 py-1 rounded-full shadow-xs whitespace-nowrap">
                                    <span className="text-sm font-medium text-orange-700 dark:text-orange-300">
                                        Bewerbung zurückgezogen
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                <svg className="w-8 h-8 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                        </div>
                        
                        <div className="text-lg text-slate-600 dark:text-slate-400 mb-8 leading-relaxed text-center">
                            {normalizedFragebogenData.cancelOutroText || "Vielen Dank für deine Zeit! Du hast deine Bewerbung erfolgreich zurückgezogen. Falls du dich doch nochmal entscheidest, kannst du gerne eine neue Bewerbung einreichen."}
                        </div>
                        
                        <div className="text-sm text-slate-500 text-center">
                            Du kannst diese Seite jetzt schließen.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const renderErrorScreen = () => {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4 relative z-10">
                <StandaloneThemeToggle theme={theme} setTheme={setTheme} />
                <div className="w-full max-w-2xl">
                    <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-xs rounded-xl p-8 shadow-xl min-h-[600px] flex flex-col justify-center border border-slate-100 dark:border-slate-700">
                        {/* Progress Bar and Step Counter */}
                        <div className="mb-8">
                            <div className="flex justify-between items-center">
                                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3 mr-2.5 mx-2">
                                    <div 
                                        className="h-3 rounded-full transition-all duration-300"
                                        style={{ 
                                            width: '100%',
                                            background: 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)',
                                            boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)'
                                        }}
                                    ></div>
                                </div>
                                <div className="bg-red-100 dark:bg-red-900/30 px-3 py-1 rounded-full shadow-xs whitespace-nowrap">
                                    <span className="text-sm font-medium text-red-700 dark:text-red-300">
                                        Quiz beendet
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                        </div>
                        
                        <div className="text-lg text-slate-600 dark:text-slate-400 mb-8 leading-relaxed text-center">
                            {normalizedFragebogenData.errorMessage || "Diesmal passt es leider nicht. Versuch es ein anderes Mal"}
                        </div>
                        
                        <div className="text-sm text-slate-500 text-center">
                            Sie können diese Seite jetzt schließen.
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            {(showErrorScreen || hasFailed) && renderErrorScreen()}
            {!showErrorScreen && !hasFailed && currentStep === 1 && renderPreview()}
            {!showErrorScreen && !hasFailed && currentStep >= questionStepStart && currentStep < availabilityStep && renderQuestion()}
            {!showErrorScreen && !hasFailed && currentStep === availabilityStep && renderAvailability()}
            {!showErrorScreen && !hasFailed && currentStep === contactStep && renderContactForm()}
            {!showErrorScreen && !hasFailed && currentStep === summaryStep && renderSummary()}
            {!showErrorScreen && !hasFailed && renderFooter()}
            <WithdrawalModal
                isOpen={showWithdrawalModal}
                onClose={() => setShowWithdrawalModal(false)}
                onConfirmWithdrawal={handleConfirmWithdrawal}
                onStayInPool={handleStayInPool}
                cancelText={normalizedFragebogenData.cancelText}
                cancelOutroText={normalizedFragebogenData.cancelOutroText}
            />
        </>
    );
}
